import { TF } from 'react-translator-component';

/**
 * Qual dos dois tokens do módulo é este (MV2-1706).
 *
 * O módulo tem duas coisas com cara de token e propósitos opostos: o token da conta,
 * que eu entrego ao parceiro para que ele me convide, e o token do convite, que o
 * parceiro gera e eu apresento para aceitar. Trocar um pelo outro é o engano
 * previsível — e a resposta do servidor para o token trocado é a mesma de um token
 * inexistente, "confira se copiou o código completo", que manda procurar um erro de
 * cópia que não existe.
 *
 * Os prefixos são os do tenant-cross (TenantShareToken.PREFIX e
 * TenantBindInviteToken.PREFIX). Reconhecê-los aqui não revela nada: quem digita já
 * tem o token na mão, com o prefixo à vista.
 */

export type TokenKind = 'ACCOUNT' | 'INVITE';

const PREFIXES: Record<TokenKind, string> = {
  ACCOUNT: 'move_acc_',
  INVITE: 'move_itp_',
};

/** O tipo do token, ou nulo quando o prefixo não é conhecido. */
export const tokenKindOf = (raw: string): TokenKind | null => {
  const trimmed = (raw || '').trim();

  const found = (Object.keys(PREFIXES) as TokenKind[]).find((kind) =>
    trimmed.startsWith(PREFIXES[kind]),
  );

  return found || null;
};

/**
 * Aviso quando o campo recebeu o token do outro tipo, ou nulo.
 *
 * Nulo também quando o prefixo não é reconhecido: a tela não pode recusar por conta
 * própria um token que o servidor talvez aceite — de um formato mais antigo, por
 * exemplo. Nesse caso quem decide é o servidor, como antes.
 *
 * A mensagem nunca inclui o token: ela vai para a tela e daí para print de suporte.
 */
export const wrongTokenMessage = (raw: string, expected: TokenKind): string | null => {
  const kind = tokenKindOf(raw);

  if (!kind || kind === expected) {
    return null;
  }

  return expected === 'INVITE'
    ? `${TF('This is an account token, not an invite token.')} ${TF(
        'The invite token is the one your partner generates for you.',
      )}`
    : `${TF('This is an invite token, not an account token.')} ${TF(
        'Ask your partner for the token of their account.',
      )}`;
};
